import { useIntl } from 'react-intl';
import welcomeIllustration from '@repo/visual-assets/illustrations/welcome.png';
import welcomeInvoicingIllustration from '@repo/visual-assets/illustrations/welcome-invoicing.png';

interface UseWelcomeContentParams {
  legalFlow: string;
  ownerFirstName?: string;
  organizationName?: string;
  isEmbedded?: boolean;
  isAccountant?: boolean;
  partnerName?: string;
}

interface UseWelcomeContentResult {
  title: string;
  subtitle: string;
  featuresList: string[];
  illustrationSrc: string;
  ctaLabel: string;
  socialProofLabel: string;
  shouldShowPromoBanner: boolean;
}

export function useWelcomeContent({
  legalFlow,
  ownerFirstName,
  organizationName,
  isEmbedded,
  isAccountant,
  partnerName,
}: UseWelcomeContentParams): UseWelcomeContentResult {
  const { formatMessage } = useIntl();

  const isExistingCompanyFlow = legalFlow === 'existing_company';
  const isQontoInvoicingFlow = legalFlow === 'qonto_invoicing';
  const shouldShowPromoBanner = partnerName?.toLowerCase().includes('beglaubigt') ?? false;

  const title = isQontoInvoicingFlow
    ? formatMessage({ id: 'qonto-pa.welcome.title' }, { owner_first_name: ownerFirstName })
    : formatMessage({ id: 'welcome.new-user.title' }, { owner_first_name: ownerFirstName });

  const subtitle = (() => {
    if (isQontoInvoicingFlow) {
      return formatMessage(
        { id: 'qonto-pa.welcome.subtitle' },
        { organization_name: organizationName }
      );
    }
    if (isEmbedded) {
      return formatMessage({ id: 'existing-company.qonto-embed.welcome-screen.subtitle' });
    }
    if (isExistingCompanyFlow) {
      return formatMessage(
        { id: 'existing-company.fast-track.welcome-screen.subtitle' },
        { organization_name: organizationName }
      );
    }
    return formatMessage(
      { id: 'welcome.new-user.subtitle' },
      { organization_name: organizationName }
    );
  })();

  const featuresList = (() => {
    if (isQontoInvoicingFlow) {
      return [
        formatMessage({ id: 'qonto-pa.welcome.features.e-invoicing' }),
        formatMessage({ id: 'qonto-pa.welcome.features.invoice-sync' }),
      ];
    }
    if (isEmbedded) {
      return [
        formatMessage(
          { id: 'existing-company.qonto-embed.welcome-screen.list.item.1' },
          { partner: partnerName }
        ),
        formatMessage(
          { id: 'existing-company.qonto-embed.welcome-screen.list.item.2' },
          { partner: partnerName }
        ),
        formatMessage({ id: 'existing-company.qonto-embed.welcome-screen.list.item.3' }),
      ];
    }
    if (isAccountant) {
      return [
        formatMessage({ id: 'welcome.new-user.accountant.list.item.1' }),
        formatMessage({ id: 'welcome.new-user.accountant.list.item.2' }),
        formatMessage({ id: 'welcome.new-user.accountant.list.item.3' }),
      ];
    }
    return [
      formatMessage({ id: 'welcome.new-user.list.item.1' }),
      formatMessage({ id: 'welcome.new-user.list.item.2' }),
      formatMessage({ id: 'welcome.new-user.list.item.3' }),
    ];
  })();

  const illustrationSrc = isQontoInvoicingFlow ? welcomeInvoicingIllustration : welcomeIllustration;

  const ctaLabel = isExistingCompanyFlow
    ? formatMessage({ id: 'existing-company.fast-track.welcome-screen.cta' })
    : formatMessage({ id: 'welcome.new-user.cta' });

  const socialProofLabel = formatMessage({ id: 'welcome.new-user.socialproof' });

  return {
    ctaLabel,
    featuresList,
    illustrationSrc,
    shouldShowPromoBanner,
    socialProofLabel,
    subtitle,
    title,
  };
}
